import LoadingButton from '@mui/lab/LoadingButton';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogProps,
	DialogTitle,
	Stack,
	styled,
	Typography
} from '@mui/material';
import { useSSTranslation } from '@/i18n/useSSTranslation';
import CloseIcon from '@mui/icons-material/Close';

type ButtonColor =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';

interface ConfirmDialogProps extends DialogProps {
  testIdPrefix: string;
  title: string;
  content?: string;
  actionInProgress?: boolean;
  onCancel: (event: React.MouseEvent<HTMLElement>) => void;
  onConfirm: (event: React.MouseEvent<HTMLElement>) => void;
  children?: React.ReactNode;
  confirmButtonColor?: ButtonColor;
  disableConfirmButton?: boolean;
  hideCloseIcon?: boolean;
  cancelButtonTitle?: string;
  confirmButtonTitle?: string;
  preventBackDropClick?: boolean;
}

const ConfirmationDialog = ({
  testIdPrefix,
  title,
  content,
  actionInProgress = false,
  onCancel,
  onConfirm,
  maxWidth,
  fullWidth,
  children,
  confirmButtonColor,
  disableConfirmButton = false,
  hideCloseIcon=false,
  cancelButtonTitle,
  confirmButtonTitle,
  preventBackDropClick=false,
  ...props
}: ConfirmDialogProps) => {
  const {t} = useSSTranslation();

  const handleOnClose: DialogProps['onClose'] = (
    event: React.MouseEvent<HTMLElement>,
    reason
  ) => {
    if (reason && reason === 'backdropClick' && preventBackDropClick) return;
    onCancel(event);
  };

  const cancelTitle = cancelButtonTitle ?? t("common_notes_cancel", "Cancel");
  const confirmTitle = confirmButtonTitle ?? t("common_confirm", "Confirm");

  return (
    <DialogStyled
      onClose={handleOnClose}
      maxWidth={maxWidth ?? "sm"}
      fullWidth={fullWidth ?? true}
      disableEscapeKeyDown={actionInProgress}
      {...props}
    >
      {!actionInProgress && (
        <>
          <DialogTitle sx={{ color: "text.primary", textAlign: "left", fontWeight: 600, padding: "0 16px 8px 16px" }}>
			<HeaderWrapperStyled>
				<Typography variant="h6" fontWeight={600}>{title}</Typography>
				{
					!hideCloseIcon && 
					<Box
						onClick={onCancel}
						sx={{ height: "24px" }}
					>
						<CloseIcon 
							sx={theme => ({ 
								color: theme.palette.action.active,
								cursor: "pointer"
							})}
						/>
					</Box>
				}
			</HeaderWrapperStyled>
		  </DialogTitle>
          <DialogContent sx={{ textAlign: "left", padding: "0 16px 12px 16px" }}>
			{
				content && 
				<DialogContentText sx={{ color: "text.primary" }}>
					{content}
				</DialogContentText>
			}
            {children}
          </DialogContent>
          <DialogActionsStyled sx={{ padding: '0 16px !important' }}>
            <LoadingButtonStyled
              color="primary"
              variant="text"
              data-testid={`${testIdPrefix}CancelButton`}
              aria-label="Cancel action"
              onClick={onCancel}
            >
              {cancelTitle}
            </LoadingButtonStyled>
            <LoadingButtonStyled
              color={confirmButtonColor}
              variant="text"
              data-testid={`${testIdPrefix}ConfirmationButton`}
              aria-label="Confirm action"
              onClick={onConfirm}
              disabled={disableConfirmButton}
            >
			  {confirmTitle}
            </LoadingButtonStyled>
          </DialogActionsStyled>
        </>
      )}
    </DialogStyled>
  );
};

export default ConfirmationDialog;

const LoadingButtonStyled = styled(LoadingButton)(({theme}) => ({
	color: theme.palette.primary.main,
	borderColor: "transparent",
	textTransform: "uppercase",
	fontWeight: 600,
	"&.MuiLoadingButton-root:hover": {
		backgroundColor: "rgba(255, 158, 0, 0.08)"
	}
}));

const DialogStyled = styled(Dialog)(({theme}) => ({
	'& .MuiPaper-root': { 
		backgroundColor: theme.palette.background.paperElevation2,
		borderRadius: '4px',
		padding: '20px',
		border: '1px solid',
		borderColor: theme.palette.divider
	},
	'& .MuiPaper-root > .MuiDialogTitle-root': {
		padding: '0 16px 8px 16px'
	},
	'& .MuiPaper-root > .MuiDialogContent-root': {
		padding: '0 16px 12px 16px'
	},
	'& .MuiPaper-root > .MuiDialogActions-root': {
		padding: '0 16px !important'
	}
}));

const HeaderWrapperStyled = styled(Stack)(() => ({
	justifyContent: "space-between",
	alignItems: "center",
	paddingBottom: "4px",
	flexDirection: "row"
}));

const DialogActionsStyled = styled(DialogActions)(() => ({
	"&.MuiDialogActions-root": {
		flexDirection: "row",
		justifyContent: "flex-end",
		padding: "0 16px !important"
	}
}));